import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Camera, Pencil, Plus, Search, Trash2, Upload, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import DataTable from "../components/common/DataTable";
import { erpApi, formatDate, formatTime } from "../services/erpApi";

const emptyForm = {
  patient_uid: "",

  name: "",

  age: "",

  gender: "",

  mobile: "",

  alternate_mobile: "",

  relation_type: "",

  relation_name: "",

  photo_url: "",

  address: "",

  city: "",

  state: "",

  pincode: "",

  blood_group: "",

  aadhaar_number: "",

  occupation: "",

  referred_by: "",

  emergency_contact_name: "",

  emergency_contact_mobile: "",

  doctor_assigned: "",

  created_at: "",

  allergies: "",

  medical_history: "",

  notes: "",
};

const toDateInput = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return date.toISOString().slice(0, 10);
};

export default function Patients() {
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingPatient, setEditingPatient] = useState(null);
  const [duplicatePatient, setDuplicatePatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const navigate = useNavigate();

  const loadPatients = async (query = "") => {
    try {
      const response = await erpApi.patients(query ? { search: query } : {});
      setPatients(response.data);
    } catch {
      toast.error("Failed to load patients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => loadPatients(search), 250);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    erpApi
      .doctors({ active: true })
      .then((response) => setDoctors(response.data))
      .catch(() => {});
  }, []);

  const filteredPatients = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return patients;

    return patients.filter((patient) =>
      [
        patient.name,
        patient.mobile,
        patient.alternate_mobile,
        patient.patient_uid,
        patient.id,
        patient.doctor_assigned,
        patient.city,
        patient.aadhaar_number,
      ]
        .filter((value) => value !== undefined && value !== null)
        .join(" ")
        .toLowerCase()
        .includes(term),
    );
  }, [patients, search]);

  const stopCamera = () => {
    streamRef.current?.getTracks?.().forEach((track) => track.stop());
    streamRef.current = null;
    setCameraOpen(false);
    setCameraLoading(false);
    setCameraReady(false);
  };

  const startCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Camera not available in this browser");
      return;
    }
    try {
      setCameraLoading(true);
      setCameraReady(false);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      });
      streamRef.current = stream;
      setCameraOpen(true);
    } catch {
      toast.error("Camera permission denied or unavailable");
    } finally {
      setCameraLoading(false);
    }
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    if (!cameraReady || video.readyState < 2 || !video.videoWidth || !video.videoHeight) {
      toast.error("Camera loading hai, 1 second wait karke capture karein");
      return;
    }
    const width = video.videoWidth;
    const height = video.videoHeight;
    canvas.width = width;
    canvas.height = height;
    canvas.getContext("2d").drawImage(video, 0, 0, width, height);
    setForm({ ...form, photo_url: canvas.toDataURL("image/jpeg", 0.85) });
    stopCamera();
    toast.success("Patient photo captured");
  };

  const uploadPhoto = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((current) => ({ ...current, photo_url: String(reader.result || "") }));
    reader.onerror = () => toast.error("Failed to read image");
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    const video = videoRef.current;
    const stream = streamRef.current;
    if (!cameraOpen || !video || !stream) return;

    video.srcObject = stream;
    video
      .play()
      .then(() => {
        if (video.videoWidth && video.videoHeight) {
          setCameraReady(true);
        }
      })
      .catch(() => {
        toast.error("Camera preview start nahi ho paya");
      });
  }, [cameraOpen]);

  useEffect(
    () => () => {
      streamRef.current?.getTracks?.().forEach((track) => track.stop());
      streamRef.current = null;
    },
    [],
  );

  const submit = async (event) => {
    event.preventDefault();
    if (!form.name || !form.age || !form.gender || !form.mobile) {
      toast.error("Name, age, gender and mobile are required");
      return;
    }
    try {
      if (editingPatient) {
        await erpApi.updatePatient(editingPatient.id, {
          ...form,
          age: Number(form.age),
          created_at: form.created_at || editingPatient.created_at,
        });
        toast.success("Patient updated");
        setOpen(false);
        setEditingPatient(null);
        setForm(emptyForm);
        loadPatients(search);
        return;
      }
      const response = await erpApi.createPatient({
        ...form,
        age: Number(form.age),
        created_at: form.created_at || undefined,
      });
      toast.success("Patient registered");
      setOpen(false);
      setForm(emptyForm);
      navigate(`/patients/${response.data.id}`);
    } catch {
      toast.error("Failed to register patient");
    }
  };

  useEffect(() => {
    if (editingPatient || !form.mobile || form.mobile.length < 6) {
      setDuplicatePatient(null);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const response = await erpApi.checkPatientDuplicate(form.mobile);
        setDuplicatePatient(response.data.duplicate ? response.data.patient : null);
      } catch {
        setDuplicatePatient(null);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [form.mobile, editingPatient]);

  const deletePatient = async (patient) => {
    const ok = window.confirm(
      `Delete patient "${patient.name}"?\n\nThis will also delete this patient's bills and samples.`,
    );
    if (!ok) return;
    try {
      await erpApi.deletePatient(patient.id);
      toast.success("Patient deleted");
      loadPatients(search);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete patient");
    }
  };

  const openAddPatient = () => {
    setEditingPatient(null);
    setForm(emptyForm);
    stopCamera();
    setOpen(true);
  };

  const openEditPatient = (patient) => {
    setEditingPatient(patient);
    setForm({
      ...emptyForm,
      name: patient.name || "",
      age: String(patient.age || ""),
      gender: patient.gender || "",
      mobile: patient.mobile || "",
      alternate_mobile: patient.alternate_mobile || "",
      relation_type: patient.relation_type || "",
      relation_name: patient.relation_name || "",
      photo_url: patient.photo_url || "",
      address: patient.address || "",
      city: patient.city || "",
      state: patient.state || "",
      pincode: patient.pincode || "",
      blood_group: patient.blood_group || "",
      aadhaar_number: patient.aadhaar_number || "",
      occupation: patient.occupation || "",
      referred_by: patient.referred_by || "",
      emergency_contact_name: patient.emergency_contact_name || "",
      emergency_contact_mobile: patient.emergency_contact_mobile || "",
      doctor_assigned: patient.doctor_assigned || "",
      created_at: toDateInput(patient.created_at),
      allergies: patient.allergies || "",
      medical_history: patient.medical_history || "",
      notes: patient.notes || "",
    });
    stopCamera();
    setOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-manrope text-2xl font-bold">Patients</h1>
          <p className="text-sm text-hospital-text-muted">
            Patient profiles, visits, reports, prescriptions and billing
            history.
          </p>
        </div>
        <Dialog
          open={open}
          onOpenChange={(nextOpen) => {
            setOpen(nextOpen);
            if (!nextOpen) {
              stopCamera();
              setEditingPatient(null);
              setForm(emptyForm);
            }
          }}
        >
          <Button className="btn-primary" onClick={openAddPatient}>
            <Plus className="mr-2 h-4 w-4" />
            Add Patient
          </Button>
          <DialogContent className="flex max-h-[90vh] w-[calc(100vw-2rem)] max-w-4xl flex-col overflow-hidden p-0 sm:rounded-xl">
            <DialogHeader className="border-b border-gray-100 px-5 py-4 sm:px-6">
              <DialogTitle>
                {editingPatient ? "Edit Patient" : "Register Patient"}
              </DialogTitle>
            </DialogHeader>
            <form
              onSubmit={submit}
              className="flex min-h-0 flex-1 flex-col"
            >
              <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-y-auto px-5 py-4 sm:grid-cols-2 sm:px-6">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Mobile</Label>
                <Input
                  value={form.mobile}
                  onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                />
                {duplicatePatient && (
                  <div className="rounded-md bg-amber-50 p-2 text-xs font-medium text-amber-800">
                    Same mobile already used by{" "}
                    <Link to={`/patients/${duplicatePatient.id}`} className="underline">
                      {duplicatePatient.name}
                    </Link>
                    . Family member hai to same number allowed hai.
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>Alternate Mobile</Label>

                <Input
                  value={form.alternate_mobile}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      alternate_mobile: e.target.value,
                    })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Relation</Label>
                <Select
                  value={form.relation_type || "none"}
                  onValueChange={(value) =>
                    setForm({
                      ...form,
                      relation_type: value === "none" ? "" : value,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select relation" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not needed</SelectItem>
                    <SelectItem value="S/o">S/o</SelectItem>
                    <SelectItem value="D/o">D/o</SelectItem>
                    <SelectItem value="W/o">W/o</SelectItem>
                    <SelectItem value="C/o">C/o</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Relative Name</Label>
                <Input
                  placeholder="Father / husband / guardian name"
                  value={form.relation_name}
                  onChange={(e) => setForm({ ...form, relation_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Registration Date</Label>
                <Input
                  type="date"
                  value={form.created_at}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      created_at: e.target.value,
                    })
                  }
                />
                <p className="text-xs text-hospital-text-muted">
                  Purana record entry karte time actual file date select karein.
                </p>
              </div>
              <div className="space-y-3 rounded-lg border border-gray-200 p-3 sm:col-span-2">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <Label>Patient Photo</Label>
                    <p className="text-xs text-hospital-text-muted">
                      File se upload karein ya camera se live photo lein.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button type="button" variant="outline" onClick={() => document.getElementById("patient-photo-upload")?.click()}>
                      <Upload className="mr-2 h-4 w-4" />
                      Upload
                    </Button>
                    <Button type="button" variant="outline" onClick={startCamera} disabled={cameraLoading}>
                      <Camera className="mr-2 h-4 w-4" />
                      {cameraLoading ? "Opening..." : "Camera"}
                    </Button>
                    {form.photo_url && (
                      <Button type="button" variant="outline" onClick={() => setForm({ ...form, photo_url: "" })}>
                        <X className="mr-2 h-4 w-4" />
                        Remove
                      </Button>
                    )}
                  </div>
                  <Input id="patient-photo-upload" type="file" accept="image/*" className="hidden" onChange={uploadPhoto} />
                </div>
                {form.photo_url && (
                  <img
                    src={form.photo_url}
                    alt="Patient"
                    className="h-28 w-28 rounded-lg border border-gray-200 object-cover"
                  />
                )}
                {cameraOpen && (
                  <div className="space-y-3 rounded-lg bg-slate-50 p-3">
                    <video
                      ref={videoRef}
                      className="max-h-72 w-full rounded-lg bg-black object-contain"
                      autoPlay
                      playsInline
                      muted
                      onLoadedMetadata={() => setCameraReady(true)}
                      onCanPlay={() => setCameraReady(true)}
                    />
                    <canvas ref={canvasRef} className="hidden" />
                    <p className="text-xs text-hospital-text-muted">
                      {cameraReady ? "Camera ready hai. Ab photo capture kar sakte hain." : "Camera start ho raha hai..."}
                    </p>
                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="outline" onClick={stopCamera}>
                        Close Camera
                      </Button>
                      <Button type="button" className="btn-primary" onClick={capturePhoto} disabled={!cameraReady}>
                        Capture Photo
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>Age</Label>
                <Input
                  type="number"
                  value={form.age}
                  onChange={(e) => setForm({ ...form, age: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Gender</Label>
                <Select
                  value={form.gender}
                  onValueChange={(value) => setForm({ ...form, gender: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Doctor Assigned</Label>
                <Select
                  value={form.doctor_assigned || "none"}
                  onValueChange={(value) =>
                    setForm({
                      ...form,
                      doctor_assigned: value === "none" ? "" : value,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select doctor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not assigned</SelectItem>
                    {doctors.map((doctor) => (
                      <SelectItem key={doctor.id} value={doctor.name}>
                        {doctor.name} - {doctor.speciality}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
  <Label>Blood Group</Label>

  <Select
    value={form.blood_group || "none"}
    onValueChange={(value) =>
      setForm({
        ...form,
        blood_group:
          value === "none" ? "" : value,
      })
    }
  >
    <SelectTrigger>
      <SelectValue placeholder="Blood Group" />
    </SelectTrigger>

    <SelectContent>
      <SelectItem value="none">
        Not Known
      </SelectItem>

      <SelectItem value="A+">A+</SelectItem>
      <SelectItem value="A-">A-</SelectItem>
      <SelectItem value="B+">B+</SelectItem>
      <SelectItem value="B-">B-</SelectItem>
      <SelectItem value="AB+">AB+</SelectItem>
      <SelectItem value="AB-">AB-</SelectItem>
      <SelectItem value="O+">O+</SelectItem>
      <SelectItem value="O-">O-</SelectItem>
    </SelectContent>
  </Select>
</div>
              <div className="space-y-2">
                <Label>Address</Label>
                <Input
                  value={form.address}
                  onChange={(e) =>
                    setForm({ ...form, address: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
  <Label>City</Label>

  <Input
    value={form.city}
    onChange={(e) =>
      setForm({
        ...form,
        city: e.target.value,
      })
    }
  />
</div>

<div className="space-y-2">
  <Label>State</Label>

  <Input
    value={form.state}
    onChange={(e) =>
      setForm({
        ...form,
        state: e.target.value,
      })
    }
  />
</div>

<div className="space-y-2">
  <Label>Pincode</Label>

  <Input
    value={form.pincode}
    onChange={(e) =>
      setForm({
        ...form,
        pincode: e.target.value,
      })
    }
  />
</div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Notes</Label>
                <Input
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
  <Label>Medical History</Label>

  <Input
    value={form.medical_history}
    onChange={(e) =>
      setForm({
        ...form,
        medical_history: e.target.value,
      })
    }
  />
</div>

<div className="space-y-2 sm:col-span-2">
  <Label>Allergies</Label>

  <Input
    value={form.allergies}
    onChange={(e) =>
      setForm({
        ...form,
        allergies: e.target.value,
      })
    }
  />
</div>

<div className="space-y-2">
  <Label>Emergency Contact Name</Label>

  <Input
    value={form.emergency_contact_name}
    onChange={(e) =>
      setForm({
        ...form,
        emergency_contact_name:
          e.target.value,
      })
    }
  />
</div>

<div className="space-y-2">
  <Label>Emergency Contact Mobile</Label>

  <Input
    value={form.emergency_contact_mobile}
    onChange={(e) =>
      setForm({
        ...form,
        emergency_contact_mobile:
          e.target.value,
      })
    }
  />
</div>
              </div>
              <div className="flex shrink-0 justify-end gap-2 border-t border-gray-100 bg-white px-5 py-4 sm:px-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setOpen(false)}
                >
                  Cancel
                </Button>
                <Button className="btn-primary">
                  {editingPatient ? "Update Patient" : "Save Patient"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border border-gray-200">
        <CardContent className="p-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-hospital-text-muted" />
            <Input
              className="pl-9"
              placeholder="Search name, mobile, patient ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="h-64 animate-pulse rounded-lg bg-white" />
      ) : (
        <DataTable
          title={`Patient Registry (${filteredPatients.length})`}
          rows={filteredPatients}
          empty="No patients found"
          columns={[
            {
              key: "patient_uid",
              label: "Patient ID",
              render: (row) => row.patient_uid || row.id.slice(0, 8),
            },
            {
              key: "name",
              label: "Patient",
              render: (row) => (
                <div className="flex items-center gap-3">
                  {row.photo_url ? (
                    <img src={row.photo_url} alt={row.name} className="h-10 w-10 rounded-full object-cover" />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-hospital-primary-light" />
                  )}
                  <div className="min-w-0">
                    <Link
                      to={`/patients/${row.id}`}
                      className="block font-semibold text-hospital-primary"
                    >
                      {row.name}
                    </Link>
                    {row.relation_type && row.relation_name && (
                      <span className="block text-xs text-hospital-text-muted">
                        {row.relation_type} {row.relation_name}
                      </span>
                    )}
                  </div>
                </div>
              ),
            },
            { key: "mobile", label: "Mobile" },
            {
              key: "age",
              label: "Age/Gender",
              render: (row) => `${row.age} / ${row.gender}`,
            },
            {
              key: "doctor_assigned",
              label: "Doctor",
              render: (row) => row.doctor_assigned || "-",
            },
            {
              key: "created_at",
              label: "Registered",
              render: (row) => formatDate(row.created_at),
            },
            {
              key: "registered_time",
              label: "Time",
              render: (row) => formatTime(row.created_at),
            },
            {
              key: "action",
              label: "Action",
              render: (row) => (
                <div className="flex items-center gap-2">
                  <Link
                    className="text-sm font-medium text-hospital-primary"
                    to={`/billing/${row.id}`}
                  >
                    Create Bill
                  </Link>
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Edit patient"
                    onClick={() => openEditPatient(row)}
                  >
                    <Pencil className="h-4 w-4 text-hospital-primary" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Delete patient"
                    onClick={() => deletePatient(row)}
                  >
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </Button>
                </div>
              ),
            },
          ]}
        />
      )}
    </div>
  );
}
